import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Lock, CreditCard, Phone, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SubscriptionGate - Wrapper component that checks subscription status
 * Shows restricted view for suspended/expired accounts
 * Forces logout for staff when company is suspended
 */
const SubscriptionGate = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const checkStatus = useCallback(async () => {
    // Skip check for platform admins
    if (user?.role === 'platform_super_admin') {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/tenant/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setStatus(data);

        // Force logout for staff/hotel if company is suspended
        if (data.force_logout && (user?.role === 'therapist' || user?.role === 'therapist_shared' || user?.role === 'hotel')) {
          localStorage.clear();
          if (onLogout) onLogout();
          window.location.href = '/login?reason=suspended';
          return;
        }
      }
    } catch (error) {
      console.error('Status check failed:', error);
    } finally {
      setLoading(false);
      setChecking(false);
    }
  }, [user, onLogout]);

  useEffect(() => {
    checkStatus();
    
    // Check status every 30 seconds
    const interval = setInterval(checkStatus, 30 * 1000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  const handleRefresh = () => {
    setChecking(true);
    checkStatus();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground animate-pulse">Checking account status...</div>
      </div>
    );
  }

  // Show suspended view for tenant admins
  if (status?.status === 'suspended' && user?.role === 'super_admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950 dark:to-orange-950 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-red-200 dark:border-red-800">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl text-red-700 dark:text-red-400">Account Suspended</CardTitle>
            <CardDescription className="text-red-600/80 dark:text-red-400/80">
              Your organization's account has been suspended by the platform administrator.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300">
                <strong>Reason:</strong> {status.message || 'Please contact support for more information.'}
              </p>
            </div>

            <div className="space-y-3">
              <Button 
                className="w-full" 
                variant="default"
                onClick={() => navigate('/admin/my-plan')}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                View My Plan
              </Button>
              
              <Button 
                className="w-full" 
                variant="outline"
                onClick={handleRefresh}
                disabled={checking}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
                {checking ? 'Checking...' : 'Check Status Again'}
              </Button>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm text-muted-foreground mb-3">Need help? Contact WellNest Support:</p>
              <div className="flex flex-col gap-2 text-sm">
                <a href="mailto:hello@wellnesttechnologies.com" className="flex items-center gap-2 text-primary hover:underline">
                  <Mail className="w-4 h-4" />
                  hello@wellnesttechnologies.com
                </a>
                <a href="tel:+918800977146" className="flex items-center gap-2 text-primary hover:underline">
                  <Phone className="w-4 h-4" />
                  +91 8800977146
                </a>
              </div>
            </div>

            <Button 
              variant="ghost" 
              className="w-full text-muted-foreground"
              onClick={() => {
                localStorage.clear();
                if (onLogout) onLogout();
                navigate('/login');
              }}
            >
              Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show expired view for tenant admins
  if (status?.status === 'expired' && user?.role === 'super_admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-amber-200 dark:border-amber-800">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <CardTitle className="text-2xl text-amber-700 dark:text-amber-400">Subscription Expired</CardTitle>
            <CardDescription className="text-amber-600/80 dark:text-amber-400/80">
              Your subscription has expired. Renew now to continue using all features.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {status.days_overdue && (
              <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Your subscription expired <strong>{status.days_overdue} days ago</strong>. 
                  Renew now to avoid data loss.
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Button 
                className="w-full bg-amber-600 hover:bg-amber-700" 
                onClick={() => navigate('/admin/my-plan')}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Renew Subscription
              </Button>
              
              <Button 
                className="w-full" 
                variant="outline"
                onClick={handleRefresh}
                disabled={checking}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
                {checking ? 'Checking...' : 'Check Status Again'}
              </Button>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm text-muted-foreground mb-3">Need help? Contact WellNest Support:</p>
              <div className="flex flex-col gap-2 text-sm">
                <a href="mailto:hello@wellnesttechnologies.com" className="flex items-center gap-2 text-primary hover:underline">
                  <Mail className="w-4 h-4" />
                  hello@wellnesttechnologies.com
                </a>
                <a href="tel:+918800977146" className="flex items-center gap-2 text-primary hover:underline">
                  <Phone className="w-4 h-4" />
                  +91 8800977146
                </a>
              </div>
            </div>

            <Button 
              variant="ghost" 
              className="w-full text-muted-foreground"
              onClick={() => {
                localStorage.clear();
                if (onLogout) onLogout();
                navigate('/login');
              }}
            >
              Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Normal view - render children
  return children;
};

export default SubscriptionGate;
